import { createTheme, type Theme } from "@mui/material/styles";
import {
  createSQYPingTheme,
  sqypingTheme,
} from "@omichalo/sqyping-mui-theme";

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    section: true;
    dialog: true;
  }
}

type ColorMode = "light" | "dark";

const componentOverrides = {
  MuiDialogContentText: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) =>
        theme.palette.mode === "dark"
          ? { color: theme.palette.grey[300] }
          : {},
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({
        theme,
        ownerState,
      }: {
        theme: Theme;
        ownerState: { variant?: string };
      }) => {
        const isDark = theme.palette.mode === "dark";
        const base = { borderRadius: 16 };
        if (ownerState.variant === "section") {
          return {
            ...base,
            padding: "1.5rem",
            boxShadow: isDark
              ? "0 4px 16px rgba(0, 0, 0, 0.45)"
              : "0 4px 12px rgba(0, 0, 0, 0.05)",
            backgroundColor: isDark
              ? theme.palette.background.paper
              : "#fff",
            color: theme.palette.text.primary,
          };
        }
        if (ownerState.variant === "dialog") {
          return {
            ...base,
            borderRadius: 12,
            padding: "2rem",
            boxShadow: isDark
              ? "0 8px 28px rgba(0, 0, 0, 0.55)"
              : "0 6px 18px rgba(0, 0, 0, 0.15)",
            backgroundColor: isDark
              ? theme.palette.background.paper
              : "#fefefe",
            color: theme.palette.text.primary,
          };
        }
        return base;
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({
        theme,
        ownerState,
      }: {
        theme: Theme;
        ownerState: {
          disabled?: boolean;
          variant?: string;
        };
      }) => {
        const primaryMain =
          theme.palette?.primary?.main ?? sqypingTheme.palette.primary.main;
        const isDark = theme.palette.mode === "dark";
        const isContained = ownerState.variant === "contained";
        const isOutlined = ownerState.variant === "outlined";
        const disabled = Boolean(ownerState.disabled);

        const darkContainedDisabled =
          isDark && disabled && isContained
            ? {
                opacity: 1,
                color: theme.palette.grey[50],
                backgroundColor: theme.palette.grey[800],
              }
            : {};

        // Outlined en sombre : bordure + texte trop faibles par défaut / disabled.
        const darkOutlined =
          isDark && isOutlined
            ? disabled
              ? {
                  opacity: 1,
                  color: `${theme.palette.grey[300]} !important`,
                  borderColor: `${theme.palette.grey[600]} !important`,
                  backgroundColor: "rgba(255,255,255,0.04)",
                }
              : {
                  color: theme.palette.common.white,
                  borderColor: theme.palette.grey[400],
                  backgroundColor: "rgba(255,255,255,0.04)",
                  "&:hover": {
                    borderColor: theme.palette.common.white,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    color: theme.palette.common.white,
                  },
                }
            : {};

        return {
          "&.match-action-button": {
            "&::after": {
              display: "none",
            },
            "&:hover::after": {
              display: "none",
            },
            "&:hover": {
              backgroundColor: `${primaryMain} !important`,
              color: "#fff !important",
              boxShadow: "none !important",
              filter: "none !important",
            },
            "&:focus-visible": {
              boxShadow: "none !important",
              outline: "none !important",
            },
            "&:active": {
              boxShadow: "none !important",
              backgroundColor: `${primaryMain} !important`,
            },
          },
          ...darkContainedDisabled,
          ...darkOutlined,
        };
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) =>
        theme.palette.mode === "dark"
          ? {
              bgcolor: theme.palette.grey[800],
            }
          : {},
    },
  },
};

/**
 * Thème applicatif : @omichalo/sqyping-mui-theme + surcharges métier minimales.
 * getTheme(mode) pour le mode clair/sombre.
 */
export function getTheme(mode: ColorMode) {
  return createTheme(createSQYPingTheme(mode), {
    components: componentOverrides,
  });
}

/** Thème statique clair (hors ColorModeProvider) pour rétrocompatibilité ponctuelle. */
const theme = createTheme(sqypingTheme, {
  components: componentOverrides,
});

export default theme;
